import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

import { useAppDispatch, useAppSelector } from "../../redux";
import { getMetricNames, getTimeseries, getMetricTags, clearAll } from "../../redux/reducers/customMetricsSlice";
import { Button, Card, EmptyState, Spinner, Text, View, Badge, DropdownField, TabPanel } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { COLORS } from "../../utils/chartData";
import { useTheme } from "../../contexts/ThemeContext";

type TimeRange = "1h" | "6h" | "24h" | "7d" | "30d";

const TIME_RANGE_OPTIONS: { label: string; value: TimeRange }[] = [
  { label: "Last Hour", value: "1h" },
  { label: "Last 6 Hours", value: "6h" },
  { label: "Last 24 Hours", value: "24h" },
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 30 Days", value: "30d" },
];

const STEP_MAP: Record<TimeRange, string> = {
  "1h": "1m",
  "6h": "5m",
  "24h": "15m",
  "7d": "1h",
  "30d": "6h",
};

function getDateRange(range: TimeRange): { startDate: string; endDate: string } {
  const now = new Date();
  const end = now.toISOString();
  const ms: Record<TimeRange, number> = {
    "1h": 60 * 60 * 1000,
    "6h": 6 * 60 * 60 * 1000,
    "24h": 24 * 60 * 60 * 1000,
    "7d": 7 * 24 * 60 * 60 * 1000,
    "30d": 30 * 24 * 60 * 60 * 1000,
  };
  const start = new Date(now.getTime() - ms[range]).toISOString();
  return { startDate: start, endDate: end };
}

function formatTimestamp(timestamp: number, range: TimeRange): string {
  const date = new Date(timestamp * 1000);
  if (range === "1h" || range === "6h") {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  if (range === "24h") {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleDateString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatValue(value: number): string {
  if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}K`;
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2);
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <View className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg">
      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">{label}</Text>
      {payload.map((entry: any, index: number) => (
        <View
          key={index}
          className="flex flex-row items-center gap-2"
        >
          <View
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <Text className="text-xs font-medium text-gray-900 dark:text-white">
            {entry.name}: {formatValue(entry.value)}
          </Text>
        </View>
      ))}
    </View>
  );
};

const MetricChart = ({
  metricName,
  agentId,
  timeRange,
  selectedTags,
}: {
  metricName: string;
  agentId: string;
  timeRange: TimeRange;
  selectedTags: Record<string, string>;
}) => {
  const dispatch = useAppDispatch();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [data, setData] = useState<{ chartData: any[]; seriesKeys: string[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const { startDate, endDate } = getDateRange(timeRange);
    const tags = Object.keys(selectedTags).length > 0 ? selectedTags : undefined;

    dispatch(
      getTimeseries({
        metricName,
        startDate,
        endDate,
        step: STEP_MAP[timeRange],
        agentId,
        tags,
      }),
    )
      .unwrap()
      .then((result) => {
        if (cancelled) return;
        if (!result?.result?.length) {
          setData({ chartData: [], seriesKeys: [] });
          setLoading(false);
          return;
        }

        // Build chart data from Prometheus result
        const allTimestamps = new Set<number>();
        const series = result.result;

        series.forEach((s) => {
          s.values.forEach(([ts]) => allTimestamps.add(ts));
        });

        const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b);
        const seriesKeys: string[] = [];

        // Build series names from labels (excluding system labels)
        const systemLabels = new Set([
          "__name__",
          "metric_name",
          "metric_type",
          "organization_id",
          "agent_id",
          "environment_id",
          "session_id",
          "job",
          "instance",
        ]);

        series.forEach((s, idx) => {
          const userLabels = Object.entries(s.metric)
            .filter(([k]) => !systemLabels.has(k))
            .map(([, v]) => v);
          const name = userLabels.length > 0 ? userLabels.join(" / ") : `series-${idx + 1}`;
          seriesKeys.push(name);
        });

        const chartData = sortedTimestamps.map((ts) => {
          const point: Record<string, any> = { time: formatTimestamp(ts, timeRange) };
          series.forEach((s, idx) => {
            const match = s.values.find(([t]) => t === ts);
            point[seriesKeys[idx]] = match ? parseFloat(match[1]) : null;
          });
          return point;
        });

        setData({ chartData, seriesKeys });
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to fetch data");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, metricName, agentId, timeRange, selectedTags]);

  if (loading) {
    return (
      <Card className="p-6">
        <Text className="text-sm font-medium text-gray-900 dark:text-white mb-4">{metricName}</Text>
        <View className="flex items-center justify-center h-48">
          <Spinner size={16} />
        </View>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <Text className="text-sm font-medium text-gray-900 dark:text-white mb-4">{metricName}</Text>
        <View className="flex items-center justify-center h-48">
          <Text className="text-xs text-red-500">{error}</Text>
        </View>
      </Card>
    );
  }

  if (!data?.chartData?.length) {
    return (
      <Card className="p-6">
        <Text className="text-sm font-medium text-gray-900 dark:text-white mb-4">{metricName}</Text>
        <View className="flex items-center justify-center h-48">
          <Text className="text-xs text-gray-400 dark:text-gray-500">No data for this time range</Text>
        </View>
      </Card>
    );
  }

  const gridColor = isDark ? "#374151" : "#E5E7EB";
  const textColor = isDark ? "#9CA3AF" : "#6B7280";

  return (
    <Card className="p-6">
      <View className="flex flex-row items-center justify-between mb-4">
        <Text className="text-sm font-medium text-gray-900 dark:text-white">{metricName}</Text>
        {data.seriesKeys.length > 1 && <Badge variant="default">{data.seriesKeys.length} series</Badge>}
      </View>
      <ResponsiveContainer
        width="100%"
        height={240}
      >
        <LineChart
          data={data.chartData}
          margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={gridColor}
          />
          <XAxis
            dataKey="time"
            tick={{ fill: textColor, fontSize: 10 }}
            tickLine={{ stroke: gridColor }}
            axisLine={{ stroke: gridColor }}
          />
          <YAxis
            tick={{ fill: textColor, fontSize: 10 }}
            tickLine={{ stroke: gridColor }}
            axisLine={{ stroke: gridColor }}
            tickFormatter={formatValue}
            width={50}
          />
          <Tooltip content={<CustomTooltip />} />
          {data.seriesKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {data.seriesKeys.map((key, idx) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={COLORS[idx % COLORS.length]}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};

export const AgentCustomMetricsPage = () => {
  const dispatch = useAppDispatch();
  const { agentId } = useParams<{ agentId: string }>();
  const { metricNames, namesLoading, namesError, tags, tagsLoading } = useAppSelector((state) => state.customMetrics);

  const [timeRange, setTimeRange] = useState<TimeRange>("24h");
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<Record<string, string>>({});

  useEffect(() => {
    if (agentId) {
      dispatch(getMetricNames({ agentId }));
    }
    return () => {
      dispatch(clearAll());
    };
  }, [dispatch, agentId]);

  // Fetch tags when a metric is selected
  useEffect(() => {
    if (selectedMetric && agentId) {
      dispatch(getMetricTags({ metricName: selectedMetric, agentId }));
      setSelectedTags({});
    }
  }, [dispatch, selectedMetric, agentId]);

  const displayedMetrics = useMemo(() => {
    if (selectedMetric) return [selectedMetric];
    return metricNames;
  }, [metricNames, selectedMetric]);

  const handleRefresh = () => {
    if (agentId) {
      dispatch(getMetricNames({ agentId }));
    }
  };

  const handleTagChange = (tagKey: string, value: string) => {
    setSelectedTags((prev) => {
      if (!value) {
        const next = { ...prev };
        delete next[tagKey];
        return next;
      }
      return { ...prev, [tagKey]: value };
    });
  };

  if (namesLoading) {
    return (
      <TabPanel>
        <View className="flex items-center justify-center h-64">
          <Spinner size={24} />
        </View>
      </TabPanel>
    );
  }

  if (namesError) {
    return (
      <TabPanel>
        <View className="flex flex-col items-center justify-center h-64 gap-3">
          <Icon
            name="error"
            size={32}
            className="text-red-400"
          />
          <Text className="text-sm text-red-500">{namesError}</Text>
          <Button
            variant="neutral"
            size="sm"
            outline
            onClick={handleRefresh}
          >
            Retry
          </Button>
        </View>
      </TabPanel>
    );
  }

  if (!metricNames.length) {
    return (
      <TabPanel>
        <EmptyState
          icon="activity"
          title="No Custom Metrics"
          description="Track custom data points like conversion rates, response quality, or business KPIs. Metrics appear here once your agent starts reporting them."
          actionLabel="Read the docs"
          actionHref="https://docs.voicerun.com/observability/custom-metrics"
        />
      </TabPanel>
    );
  }

  const tagEntries = Object.entries(tags);

  return (
    <TabPanel>
      {/* Controls */}
      <View className="flex flex-row items-center justify-between flex-wrap gap-3">
        <View className="flex flex-row items-center gap-3 flex-wrap">
          {/* Metric filter */}
          <DropdownField
            className="w-48"
            placeholder="All Metrics"
            value={selectedMetric || ""}
            onChange={(value) => setSelectedMetric(value || null)}
            options={[{ label: "All Metrics", value: "" }, ...metricNames.map((name) => ({ label: name, value: name }))]}
          />

          {/* Time range */}
          <DropdownField
            className="w-40"
            value={timeRange}
            onChange={(value) => setTimeRange(value as TimeRange)}
            options={TIME_RANGE_OPTIONS}
          />

          {/* Tag filters - only show when a single metric is selected */}
          {selectedMetric &&
            !tagsLoading &&
            tagEntries.map(([tagKey, tagValues]) => (
              <DropdownField
                key={tagKey}
                className="w-40"
                placeholder={tagKey}
                value={selectedTags[tagKey] || ""}
                onChange={(value) => handleTagChange(tagKey, value)}
                options={[{ label: `All ${tagKey}`, value: "" }, ...tagValues.map((v) => ({ label: v, value: v }))]}
              />
            ))}
        </View>

        <View className="flex flex-row items-center gap-2">
          <Text className="text-xs text-gray-400 dark:text-gray-500">
            {displayedMetrics.length} metric{displayedMetrics.length !== 1 ? "s" : ""}
          </Text>
          <Button
            variant="neutral"
            size="sm"
            outline
            onClick={handleRefresh}
          >
            <Icon
              name="refresh"
              size={14}
            />
            Refresh
          </Button>
        </View>
      </View>

      {/* Charts */}
      <View className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {agentId &&
          displayedMetrics.map((metricName) => (
            <MetricChart
              key={`${metricName}-${timeRange}-${JSON.stringify(selectedTags)}`}
              metricName={metricName}
              agentId={agentId}
              timeRange={timeRange}
              selectedTags={selectedTags}
            />
          ))}
      </View>
    </TabPanel>
  );
};
